/**
 * Plan Registry - authoritative source of truth for PUBLIC pricing.
 *
 * Architecture:
 *   LAUNCH_PLAN_REGISTRY - the single public commercial authority.
 *
 * Internal execution economics are server-side and must not be duplicated here.
 * Historical/future plan experiments belong in explicitly historical records,
 * not in the live launch registry.
 *
 * RULE: Only getPublicPlans() / getPlanByTier() are consumed by UI.
 */

import type { PlanTier } from "@/store/computeStore";
import { customerCreditsDisplayFromAcu } from "@/billing/customerCredits";

// ── Public Plan Shape ────────────────────────────────────────────

export interface PublicPlan {
  /** Internal tier identifier */
  tier: PlanTier;
  /** Display label */
  label: string;
  /** Monthly price in USD cents (integer). 0 = free. -1 = custom/contact */
  monthlyPriceCents: number;
  /** Monthly price formatted for display */
  monthlyPriceDisplay: string;
  /** ACU allocation per month. 0 = custom */
  monthlyAcu: number;
  /** Display-formatted ACU */
  acuDisplay: string;
  /** Short positioning tagline */
  tagline: string;
  /** Shown below price */
  subtext: string;
  /** Highlight as most popular */
  mostPopular: boolean;
  /** Highlight badge text (e.g. "Most Popular") */
  badge?: string;
  /** CTA button text */
  ctaLabel: string;
  /** Whether this plan has a public checkout flow */
  hasCheckout: boolean;
}

// ── Add-on Shape ─────────────────────────────────────────────────

export interface PlanAddon {
  id: string;
  label: string;
  description: string;
  monthlyPriceCents: number;
  monthlyPriceDisplay: string;
  availableFrom: PlanTier[];
}

// ── Top-up Package Shape ─────────────────────────────────────────

export type TopupTier = PlanTier;

export interface TopupPackage {
  id:                  string;
  label:               string;
  acuAmount:           number;
  priceCents:          number;
  priceDisplay:        string;
  /** Price per 1,000 credits - shown for transparency */
  pricePer1000Display: string;
  featured:            boolean;
  /** Intended use case hint */
  purpose:             string;
  /** Max outstanding top-up ACU - enforced at purchase (uniform, anti-abuse) */
  stackingCapAcu:      number;
}

// ─────────────────────────────────────────────────────────────────
// TOP-UP PACKAGE REGISTRY (single commercial catalog)
//
// Rules:
//   - Free users cannot purchase top-ups.
//   - Starter, Pro and Studio may purchase top-ups.
//   - Top-ups never change subscription tier or unlock a gated capability.
//   - The server-side purchase guard is the final enforcement boundary.
//
// This catalog contains commercial prices only. Provider cost and margin are governed by the
// server economic ledger and must never be duplicated here.
// ─────────────────────────────────────────────────────────────────

export const TOPUP_STACKING_CAP_ACU = 50_000;

export const ALL_TOPUP_PACKAGES: TopupPackage[] = [
  {
    id:                  "topup_500",
    label:               "500 credits",
    acuAmount:           500,
    priceCents:          1900,        // $19 for 50 customer credits
    priceDisplay:        "$19",
    pricePer1000Display: "$380 / 1,000 credits",
    featured:            false,
    purpose:             "Quick boost when you run low",
    stackingCapAcu:      TOPUP_STACKING_CAP_ACU,
  },
  {
    id:                  "topup_1000",
    label:               "1,000 credits",
    acuAmount:           1000,
    priceCents:          3600,        // $36 for 100 customer credits
    priceDisplay:        "$36",
    pricePer1000Display: "$360 / 1,000 credits",
    featured:            false,
    purpose:             "A single build sprint",
    stackingCapAcu:      TOPUP_STACKING_CAP_ACU,
  },
  {
    id:                  "topup_2500",
    label:               "2,500 credits",
    acuAmount:           2500,
    priceCents:          8500,        // $85 for 250 customer credits
    priceDisplay:        "$85",
    pricePer1000Display: "$340 / 1,000 credits",
    featured:            true,
    purpose:             "Most popular",
    stackingCapAcu:      TOPUP_STACKING_CAP_ACU,
  },
  {
    id:                  "topup_10000",
    label:               "10,000 credits",
    acuAmount:           10000,
    priceCents:          31000,       // $310 for 1,000 customer credits
    priceDisplay:        "$310",
    pricePer1000Display: "$310 / 1,000 credits",
    featured:            false,
    purpose:             "A heavy build month",
    stackingCapAcu:      TOPUP_STACKING_CAP_ACU,
  },
];

// Backward-compatible alias (used by PricingPage for "show all" view)
export const TOPUP_PACKAGES = ALL_TOPUP_PACKAGES;

export function getTopupStackingCap(_tier?: PlanTier): number {
  // Single uniform cap on outstanding top-up ACU for all users.
  return TOPUP_STACKING_CAP_ACU;
}

// ─────────────────────────────────────────────────────────────────
// LAUNCH PRICING REGISTRY
// Strategic launch-market pricing - optimised for adoption and retention.
// These are NOT cost-based ceilings - internal cost governance lives
// in ACUEconomicsGovernor. These are the public promise to users.
// ─────────────────────────────────────────────────────────────────

export const LAUNCH_PLAN_REGISTRY: Record<PlanTier, PublicPlan> = {

  free: {
    tier:               "free",
    label:              "Free",
    monthlyPriceCents:  0,
    monthlyPriceDisplay: "$0",
    monthlyAcu:         0,
    // Free = 0 provider-funded AI ACU (owner-directed, launch capital protection). The Free ACCOUNT is for
    // product exploration (non-AI); real AI generation requires a paid plan. History: 200 -> 100 -> 60 -> 0.
    acuDisplay:         "AI generation requires a paid plan",
    tagline:            "Prove Nuvra on your own idea",
    subtext:            "Real builds with live interactive preview, documents and books, voice input, code export, backups, shared hosting",
    mostPopular:        false,
    ctaLabel:           "Get Started",
    hasCheckout:        false,
  },

  starter: {
    tier:               "starter",
    label:              "Starter",
    monthlyPriceCents:  2900,       // $29 / month
    monthlyPriceDisplay: "$29",
    monthlyAcu:         1_000,
    acuDisplay:         `${customerCreditsDisplayFromAcu(1_000)} credits / month`,
    tagline:            "For creators and indie builders",
    subtext:            "Everything in Free with more room, plus image, speech and music generation, Android TWA builds, version rollback, and top-ups that never expire",
    mostPopular:        false,
    ctaLabel:           "Start Building",
    hasCheckout:        true,
  },

  pro: {
    tier:               "pro",
    label:              "Pro",
    monthlyPriceCents:  9900,       // $99 / month
    monthlyPriceDisplay: "$99",
    monthlyAcu:         4_000,
    acuDisplay:         `${customerCreditsDisplayFromAcu(4_000)} credits / month`,
    tagline:            "Full-stack software, sold and shipped",
    subtext:            "Full-stack generation with a real database, custom domains, native Android and iOS builds, store readiness, no watermark, and marketplace selling at 80% revenue share",
    mostPopular:        true,
    badge:              "Most Popular",
    ctaLabel:           "Go Pro",
    hasCheckout:        true,
  },

  studio: {
    tier:               "studio",
    label:              "Studio",
    monthlyPriceCents:  29900,      // $299 / month
    monthlyPriceDisplay: "$299",
    monthlyAcu:         13_000,
    acuDisplay:         `${customerCreditsDisplayFromAcu(13_000)} credits / month`,
    tagline:            "For agencies and production businesses",
    subtext:            "Teams with roles, autonomous agents, white-label client delivery, priority AI queue, revenue analytics, organization-enforced MFA",
    mostPopular:        false,
    ctaLabel:           "Launch Studio",
    hasCheckout:        true,
  },

  enterprise: {
    tier:               "enterprise",
    label:              "Enterprise",
    monthlyPriceCents:  -1,         // Custom - contact sales
    monthlyPriceDisplay: "Custom",
    monthlyAcu:         0,          // Negotiated
    acuDisplay:         "Custom credits",
    tagline:            "For enterprises and regulated organizations",
    subtext:            "SSO/SAML and SCIM provisioning, exportable audit logs, regulated mode and data controls, SLA support, custom credits",
    mostPopular:        false,
    ctaLabel:           "Contact Sales",
    hasCheckout:        false,       // Relationship-driven - no self-serve
  },
};

// ── Add-ons ──────────────────────────────────────────────────────

export const PLAN_ADDONS: PlanAddon[] = [
  {
    id:                   "white_label",
    label:                "White-Label Branding",
    description:          "Remove Nuvra branding from previews, exports, and client deliverables",
    monthlyPriceCents:    9900,     // $99 / month
    monthlyPriceDisplay:  "$99/mo",
    availableFrom:        ["studio", "enterprise"],
  },
  {
    id:                   "compliance_pack",
    label:                "Compliance Pack",
    description:          "Audit exports, regulated mode, compliance scans, and policy reports",
    monthlyPriceCents:    19900,    // $199 / month
    monthlyPriceDisplay:  "$199/mo",
    availableFrom:        ["enterprise"],
  },
];

// ── Top-up Packages ──────────────────────────────────────────────
// PRICING STATUS: calibrated. All four packages sit above the most expensive
// subscription per-ACU rate (Starter $0.029/ACU), so a subscription is always the
// better value and a top-up is a pure-margin convenience. Purchasing goes live once
// the top-up checkout route + durable pending store are wired (see topupEngine).
// ── Public Accessors ─────────────────────────────────────────────

/** Returns all tiers ordered for public display (free → enterprise). */
export function getPublicPlans(): PublicPlan[] {
  const order: PlanTier[] = ["free", "starter", "pro", "studio", "enterprise"];
  return order.map(t => LAUNCH_PLAN_REGISTRY[t]);
}

/** Returns the public-facing plan config for a given tier. */
export function getPlanByTier(tier: PlanTier): PublicPlan {
  return LAUNCH_PLAN_REGISTRY[tier];
}

/** Returns public monthly price in USD cents (-1 for custom). */
export function getMonthlyPriceCents(tier: PlanTier): number {
  return LAUNCH_PLAN_REGISTRY[tier].monthlyPriceCents;
}

/** Returns the ACU allowance for a given tier (0 = custom). */
export function getAcuAllowance(tier: PlanTier): number {
  return LAUNCH_PLAN_REGISTRY[tier].monthlyAcu;
}

/**
 * Returns the top-up catalog for a paid plan. Free and enterprise accounts do not
 * receive a self-serve top-up catalog. The server enforces the same rule; this is
 * intentionally mirrored in the registry so UI, helpers and backend policy cannot
 * disagree.
 */
export function getTopupPackages(tier: PlanTier = "starter"): TopupPackage[] {
  if (tier === "free" || tier === "enterprise") return [];
  return ALL_TOPUP_PACKAGES;
}

/** Returns featured top-up package for a given tier. */
export function getFeaturedTopup(tier: PlanTier = "starter"): TopupPackage {
  const packages = getTopupPackages(tier);
  const featured = packages.find(p => p.featured);
  if (!featured) throw new Error(`No top-up package is available for paid tier: ${tier}`);
  return featured;
}

/** Returns addons available for a given tier. */
export function getAvailableAddons(tier: PlanTier): PlanAddon[] {
  return PLAN_ADDONS.filter(a => a.availableFrom.includes(tier));
}

/**
 * Determine if a tier upgrade is required to access a given monthly ACU amount.
 * Used to suggest upgrade paths when users are approaching their limit.
 */
export function suggestUpgradeTier(currentTier: PlanTier, neededAcu: number): PlanTier | null {
  const order: PlanTier[] = ["free", "starter", "pro", "studio", "enterprise"];
  const startIdx = order.indexOf(currentTier);
  for (let i = startIdx + 1; i < order.length - 1; i++) {
    const tier = order[i];
    if (LAUNCH_PLAN_REGISTRY[tier].monthlyAcu >= neededAcu) return tier;
  }
  return null;
}

/**
 * Returns the marketplace revenue share percentage (platform commission).
 * Currently 20% - matches pricingService.ts env-driven configuration.
 * Exposed here so UI can display it consistently without hardcoding.
 */
export const MARKETPLACE_REVENUE_SHARE_PERCENT = 20;
export const CREATOR_REVENUE_SHARE_PERCENT     = 80;
